// Client half of the ENG-36 correlation id (openspec:
// observability-failure-taxonomy). Installed once from
// instrumentation-client.ts: every same-origin `/api/*` fetch carries an
// `x-request-id` the server echoes back (lib/server-error-tracking.ts) and
// tags on its Sentry event, so a failed generation can be found from either
// side with one id. No-op outside the browser; never throws; a caller that
// already set the header wins. MUST NOT import server modules.

export const REQUEST_ID_HEADER = 'x-request-id'

export function newClientRequestId(): string {
  try {
    return globalThis.crypto.randomUUID()
  } catch {
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`
  }
}

export function isSameOriginApiUrl(url: string, origin: string): boolean {
  if (url.startsWith('/api/')) return true
  return url.startsWith(`${origin}/api/`)
}

type FetchLike = typeof fetch

/** Wrap `fetch` so same-origin `/api/*` requests carry a fresh request id. Exported for tests. */
export function wrapFetchWithRequestId(orig: FetchLike, origin: string): FetchLike {
  return function fetchWithRequestId(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
    try {
      const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url
      if (isSameOriginApiUrl(url, origin)) {
        const headers = new Headers(init?.headers ?? (input instanceof Request ? input.headers : undefined))
        if (!headers.has(REQUEST_ID_HEADER)) {
          headers.set(REQUEST_ID_HEADER, newClientRequestId())
          init = { ...init, headers }
        }
      }
    } catch {
      /* observability only */
    }
    return orig(input, init)
  } as FetchLike
}

const INSTALLED = '__stytrixRequestIdFetch'

export function installRequestIdHeader(): void {
  if (typeof window === 'undefined' || typeof window.fetch !== 'function') return
  const w = window as unknown as Record<string, unknown>
  if (w[INSTALLED]) return
  try {
    window.fetch = wrapFetchWithRequestId(window.fetch.bind(window), window.location.origin)
    w[INSTALLED] = true
  } catch {
    /* leave native fetch untouched */
  }
}
